import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import { StarsIcon, UserIcon } from '@/icons';
import { cn } from '@/lib/cn';
import { capitalize } from '@/utils';
import { Flex } from 'antd';
import type { ReactNode } from 'react';

type Props = {
  item?: {
    name: string;
    photo: string | null;
    designation: string;
    department: string;
    assessment_status: string;
  };
  actionRender?: ReactNode;
  showStatus?: boolean;
  classNames?: { wrapper?: string; titleWrapper?: string; body?: string };
  interviewType?: 'ai' | 'manual';
};

const EmpCard = ({
  item,
  actionRender,
  showStatus = true,
  classNames,
  interviewType,
}: Props) => {
  const status = item?.assessment_status?.toLowerCase();
  const statusClassMap: Record<string, string> = {
    submitted: 'status-yellow',
    in_progress: 'status-yellow',
    pending: 'status-yellow',
    passed: 'status-success',
    completed: 'status-success',
    accepted: 'status-success',
    hired: 'status-success',
    failed: 'status-error',
    canceled: 'status-error',
    cancel: 'status-error',
    red_list: 'status-error',
    no_show: 'status-slate text-white/80',
  };
  return (
    <div
      className={cn(
        'p-6 bg-theme-slate-800 h-full border border-theme-slate-700 rounded-xl space-y-5',
        classNames?.wrapper,
      )}
    >
      {interviewType === 'ai' && (
        <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-br from-purple-500/10 via-indigo-500/5 to-transparent rounded-bl-full pointer-events-none" />
      )}
      <Flex
        justify="space-between"
        align="start"
        className={cn(
          classNames?.titleWrapper,
          interviewType === 'ai' && '!mt-0',
        )}
      >
        <div className="relative">
          <CustomImage
            width={70}
            height={70}
            alt={item?.name}
            placeholder={IMAGES.avatar}
            src={item?.photo || IMAGES.avatar}
            className="rounded-full object-cover"
          />
        </div>
        <div className="flex flex-col items-end gap-2">
          {interviewType === 'ai' && (
            <div className="flex items-center gap-1.5 px-3 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 shadow-[0_0_12px_rgba(139,92,246,0.3)]">
              <StarsIcon size={12} className="text-white" />
              <span>AI Interview</span>
            </div>
          )}
          {interviewType === 'manual' && (
            <div className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-slate-300 rounded-full bg-theme-slate-700/60 border border-theme-slate-600">
              <UserIcon size={12} className="text-slate-400" />
              <span>Manual Interview</span>
            </div>
          )}
          <div className="mt-1">{actionRender}</div>
        </div>
      </Flex>
      <div className={cn('space-y-2', classNames?.body)}>
        <Flex align="center" gap={8}>
          <h3 className="text-2xl text-white font-extrabold">
            {item?.name || 'n/a'}
          </h3>
          {showStatus && (
            <span
              className={cn(
                'status capitalize',
                status && statusClassMap[status],
              )}
            >
              {capitalize(item?.assessment_status || 'n/a')}
            </span>
          )}
        </Flex>
        <div className="text-left">
          <span className="text-white text-base font-medium">Job Applied:</span>
          <h5 className="text-lg text-white font-semibold">
            {item?.designation || 'n/a'}
          </h5>
        </div>
        <Flex align="center" gap={6}>
          <span className="text-white text-base">Department:</span>
          <h5 className="text-white text-lg font-semibold">
            {item?.department || 'n/a'}
          </h5>
        </Flex>
      </div>
    </div>
  );
};

export default EmpCard;
